import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowDownToLine, Building2, Loader2 } from "lucide-react";

/**
 * Withdraw Dialog
 */
export function WithdrawDialog({ 
  open, 
  onOpenChange, 
  availableBalance, 
  withdrawAmount, 
  setWithdrawAmount, 
  onWithdraw, 
  processing 
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowDownToLine className="w-5 h-5 text-emerald-500" />
            Request Withdrawal
          </DialogTitle>
          <DialogDescription>
            Available balance: ₹{availableBalance?.toLocaleString() || 0}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label>Withdrawal Amount</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
              <Input
                type="number"
                placeholder="Enter amount"
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                className="pl-8 rounded-none"
                min="100"
                max={availableBalance}
                data-testid="withdraw-amount-input"
              />
            </div>
            <p className="text-xs text-muted-foreground">Minimum: ₹100</p>
          </div>

          {/* Quick amounts */}
          <div className="flex gap-2">
            {[500, 1000, 2000, 5000].map(amt => (
              <Button
                key={amt}
                variant={withdrawAmount === String(amt) ? "default" : "outline"}
                size="sm"
                className="rounded-none flex-1"
                onClick={() => setWithdrawAmount(String(Math.min(amt, availableBalance || 0)))}
                disabled={amt > (availableBalance || 0)}
              >
                ₹{amt}
              </Button>
            ))}
          </div>

          <Button
            className="w-full rounded-none bg-emerald-600 hover:bg-emerald-700"
            onClick={onWithdraw}
            disabled={processing || !withdrawAmount || parseFloat(withdrawAmount) < 100}
            data-testid="confirm-withdraw-btn"
          >
            {processing && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Request Withdrawal
          </Button>
          
          <p className="text-xs text-center text-muted-foreground">
            Withdrawals are processed within 2-3 business days
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Bank Details Dialog
 */
export function BankDetailsDialog({ 
  open, 
  onOpenChange, 
  bankForm, 
  setBankForm, 
  onSave, 
  processing 
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-500" />
            Add Bank Details
          </DialogTitle>
          <DialogDescription>
            Add your bank account for withdrawals
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label>Account Holder Name *</Label>
            <Input
              placeholder="As per bank records"
              value={bankForm.account_holder_name}
              onChange={(e) => setBankForm({...bankForm, account_holder_name: e.target.value})}
              className="rounded-none"
              data-testid="bank-holder-name"
            />
          </div>
          <div className="space-y-2">
            <Label>Account Number *</Label>
            <Input
              placeholder="Enter account number"
              value={bankForm.account_number}
              onChange={(e) => setBankForm({...bankForm, account_number: e.target.value})}
              className="rounded-none"
              data-testid="bank-account-number"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>IFSC Code *</Label>
              <Input
                placeholder="HDFC0001234"
                value={bankForm.ifsc_code}
                onChange={(e) => setBankForm({...bankForm, ifsc_code: e.target.value.toUpperCase()})}
                className="rounded-none"
                maxLength={11}
                data-testid="bank-ifsc"
              />
            </div>
            <div className="space-y-2">
              <Label>Bank Name *</Label>
              <Input
                placeholder="HDFC Bank"
                value={bankForm.bank_name}
                onChange={(e) => setBankForm({...bankForm, bank_name: e.target.value})}
                className="rounded-none"
                data-testid="bank-name"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>UPI ID (Optional)</Label>
            <Input
              placeholder="yourname@upi"
              value={bankForm.upi_id}
              onChange={(e) => setBankForm({...bankForm, upi_id: e.target.value})}
              className="rounded-none"
              data-testid="bank-upi"
            />
          </div>

          <Button
            className="w-full rounded-none"
            onClick={onSave}
            disabled={processing}
            data-testid="save-bank-btn"
          >
            {processing && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Save Bank Details
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
